import { supabase } from "@/platform/lib/supabase";
import { applySM2, defaultState, type SM2State } from "@/platform/learning/sm2";

export interface ReviewQuestion {
  id: string;
  question_id: string;
  due_at: string;
  ease_factor: number;
  interval_days: number;
  repetitions: number;
  question: {
    id: string;
    number: number | null;
    kind: "objective" | "discursive" | "essay";
    stem: string;
    alternatives: Array<{ label: string; text: string }> | null;
    correct_answer: string | null;
    discipline: string;
    topic: string | null;
    has_base_text: boolean;
    base_text: string | null;
    exam_year: number;
  };
}

/**
 * Carrega revisões com due_at <= agora (com limite).
 */
export async function loadDueReviews(userId: string, limit = 20): Promise<ReviewQuestion[]> {
  const nowIso = new Date().toISOString();
  const { data, error } = await supabase
    .from("einstein_reviews")
    .select(
      "id, question_id, due_at, ease_factor, interval_days, repetitions, einstein_questions!inner(id, number, kind, stem, alternatives, correct_answer, discipline, topic, has_base_text, base_text, einstein_exam_papers!inner(year))",
    )
    .eq("user_id", userId)
    .lte("due_at", nowIso)
    .order("due_at", { ascending: true })
    .limit(limit);
  if (error) throw error;
  type Raw = {
    id: string;
    question_id: string;
    due_at: string;
    ease_factor: number;
    interval_days: number;
    repetitions: number;
    einstein_questions: {
      id: string;
      number: number | null;
      kind: "objective" | "discursive" | "essay";
      stem: string;
      alternatives: Array<{ label: string; text: string }> | null;
      correct_answer: string | null;
      discipline: string;
      topic: string | null;
      has_base_text: boolean;
      base_text: string | null;
      einstein_exam_papers: { year: number };
    };
  };
  return (data as unknown as Raw[]).map((r) => ({
    id: r.id,
    question_id: r.question_id,
    due_at: r.due_at,
    ease_factor: r.ease_factor,
    interval_days: r.interval_days,
    repetitions: r.repetitions,
    question: { ...r.einstein_questions, exam_year: r.einstein_questions.einstein_exam_papers.year },
  }));
}

/**
 * Conta revisões devidas (para badge no header / dashboard).
 */
export async function countDueReviews(userId: string): Promise<number> {
  const nowIso = new Date().toISOString();
  const { count, error } = await supabase
    .from("einstein_reviews")
    .select("id", { count: "exact", head: true })
    .eq("user_id", userId)
    .lte("due_at", nowIso);
  if (error) {
    console.error("[review] count", error);
    return 0;
  }
  return count ?? 0;
}

/**
 * Atualiza linha de revisão aplicando SM-2 com a grade dada (0..5).
 */
export async function gradeReview(reviewId: string, grade: number, prev: SM2State): Promise<void> {
  const next = applySM2(prev, grade);
  const { error } = await supabase
    .from("einstein_reviews")
    .update({
      ease_factor: next.ease_factor,
      interval_days: next.interval_days,
      repetitions: next.repetitions,
      last_grade: next.last_grade,
      due_at: next.due_at,
      updated_at: new Date().toISOString(),
    })
    .eq("id", reviewId);
  if (error) throw error;
}

/**
 * Agenda revisões para uma lista de question_ids. Idempotente — se já existe
 * revisão para a tupla (user, question), não sobrescreve (evita resetar EF
 * conquistado).
 */
export async function scheduleReviews(params: {
  userId: string;
  questionIds: string[];
  initialDelayHours?: number;
}): Promise<number> {
  if (params.questionIds.length === 0) return 0;
  // Busca quais já existem
  const { data: existing } = await supabase
    .from("einstein_reviews")
    .select("question_id")
    .eq("user_id", params.userId)
    .in("question_id", params.questionIds);
  const existingSet = new Set((existing ?? []).map((r) => r.question_id as string));
  const toInsert = params.questionIds.filter((q) => !existingSet.has(q));
  if (toInsert.length === 0) return 0;

  const dueAt = new Date(Date.now() + (params.initialDelayHours ?? 24) * 3600000).toISOString();
  const initState = defaultState();
  const rows = toInsert.map((qid) => ({
    user_id: params.userId,
    question_id: qid,
    due_at: dueAt,
    interval_days: 1,
    ease_factor: initState.ease_factor,
    repetitions: 0,
  }));
  const { error } = await supabase.from("einstein_reviews").insert(rows);
  if (error) throw error;
  return rows.length;
}
